import { Arr } from '@deqo/foundation';
import { MflScope } from '@deqo/mfl-js/src/lang/MflScope';
import stringTemplate from 'string-template';
import { debounce, uuid } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { round as realRound } from '../../../platform/geom/Math';
import { ActiveObject, resolve } from '../../Container';
import { ProfileModel } from '../../ProfileModel';
import { Physical } from '../../canvas/entities/Physical';
import { alert } from '../alerts/AlertCommands';
import { LensReflector } from '../lens/LensReflector';
import { MqlUtils } from '../lens/MqlUtils';
import { ActionComputeItem, AssertComputeItem, ComputeItem, FormulaComputeItem, QueryComputeItem } from './ComputeItem';
import { SmartBox, SmartBoxComputation } from './SmartBox';

const funcs = (reflector: LensReflector) => {

    const count = (input: any) => {
        if (Array.isArray(input)) {
            return input.length;
        }
        return 0;
    };

    const first = (input: any) => {
        if (Array.isArray(input)) {
            return input[0];
        }
        return input;
    };

    const last = (input: any) => {
        if (Array.isArray(input)) {
            return input[input.length - 1];
        }
        return input;
    };

    const mode = (input: any) => {
        if (Array.isArray(input)) {
            return Arr.mode(input);
        }
        return input;
    };

    const format = (template: string, ...args: any[]) => {
        return stringTemplate(template, args);
    };

    const props = (elmts: Physical[], prop?: string) => {
        const result = [] as any[];

        if (!!prop) {
            const aspect = reflector.find(elmts, prop);
            for (const p of elmts) {
                const v = first(aspect.read(p));
                result.push(v);
            }
        }
        else {
            for (const p of elmts) {
                for (const f of p.uiFields) {
                    if (p.has(f.name)) {
                        const v = p.get(f.name);
                        if (!isNaN(v)) result.push(v);
                    }
                }
            }
        }

        return result;
    };

    const values = (elmts: Physical[], prop?: string) => {
        const result = [] as number[];

        if (!!prop) {
            const aspect = reflector.find(elmts, prop);
            for (const p of elmts) {
                const v = !!aspect ? parseFloat(`${first(aspect.read(p))}`) : 0;
                if (!isNaN(v)) result.push(v);
            }
        }
        else {
            for (const p of elmts) {
                for (const f of p.uiFields) {
                    if (p.has(f.name)) {
                        const v = parseFloat(p.get(f.name));
                        if (!isNaN(v)) result.push(v);
                    }
                }
            }
        }

        return result;
    };

    const round = (input: any, decimals: number) => {
        return realRound(Number(input) || 0, decimals);
    };

    const percent = (a: any, b: any) => {
        a = Number(a) || 0;
        b = Number(b) || 0;
        if (b === 0) return 0;
        return `${Math.round((a / b) * 100)}%`;
    };

    return { count, format, props, values, first, last, mode, round, percent };

};

export class ComputeController extends ActiveObject {

    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(LensReflector) private reflector!: LensReflector;
    @resolve(ObjectManager) private objects!: ObjectManager;
    @resolve(ProfileModel) private me!: ProfileModel;
    @resolve(MqlUtils) private mqlUtils!: MqlUtils;

    public start() {
        const syncDebounced = debounce(this.sync, 500);
        this.objects.on('updated', syncDebounced);
        syncDebounced();
    }

    private sync = () => {

        const computes = this.objects.getAll(SmartBox);
        for (const c of computes) {
            this.execute(c, 'passive');
        }
    };

    public execute(computable: Physical, type: 'active' | 'passive') {

        const updateKey = `${computable.id}/${uuid().slice(0, 8)}`;

        const vars: Record<string, any> = {
            self: computable.id,
            myId: this.me.id,
            myName: this.me.name,
        };

        const items = computable.polarize(ComputeItem);
        const results = [] as any[];

        for (const tm of items) {

            if (tm.is(QueryComputeItem)) {

                const qtm = tm.as(QueryComputeItem);
                if (!qtm.compiledCode) return;

                const scope = this.mqlUtils.createScope(vars);
                const elmts = this.objects.findAll(Physical, x => x.mounted());
                const reflecting = this.reflector.createProxies(elmts);
                const hits = reflecting.filter(x => qtm.compiledCode.test(scope, x[1])).map(x => x[0]);
                vars[tm.name] = hits;
            }
            else if (tm.is(FormulaComputeItem)) {

                const ftm = tm.as(FormulaComputeItem);
                if (!ftm.compiledCode) return;

                const scope = MflScope.createDefault()
                    .addFunctions(funcs(this.reflector))
                    .addSymbols(vars)
                    .build();

                const res = ftm.compiledCode.exec(scope);

                results.push(res);
                vars[tm.name] = res;
            }
            else if (type === 'active' && tm.is(ActionComputeItem)) {
                const atm = tm.as(ActionComputeItem);

                const [name, ...args] = atm.settings;
                const realArgs = args.map(x => {
                    // Sub in any $vars
                    return x.replaceAll(/\$[a-zA-Z0-9]+/g, (ve) => {
                        const vn = ve.slice(1);
                        if (vn in vars) {
                            const vv = vars[vn];
                            if (vv === null || vv === undefined) {
                                return '';
                            }
                            else {
                                return `${vv}`;
                            }
                        }
                        else {
                            return ve;
                        }
                    });
                });

                this.objects.update(u => {
                    u.rolling(updateKey);
                    this.dispatch(name, realArgs);
                });
            }
            else if (type === 'active' && tm.is(AssertComputeItem)) {
                const atm = tm.as(AssertComputeItem);

                const name = atm.value?.replace(/^\$/, '');
                const value = vars[name];

                if ((Array.isArray(value) && value.length === 0) || !value) {
                    if (atm.message) {
                        this.dispatch(alert, [atm.message]);
                    }
                    break;
                }
            }
        }

        if (type === 'active') {
            this.sync();
        }
        else {
            computable.as(SmartBox, sb => {
                const bestResult = results.reverse().find(x => x !== null && x !== undefined);
                const strResult = bestResult === undefined || bestResult === null ? '' : `${bestResult}`;
                SmartBoxComputation.set(sb, strResult);
            });
        }
    }
}

